import {
  ButtonContainer,
  EyeWrapper,
  Form,
  InputWrapper,
  ForgotPasswordLabel,
  ForgotPasswordWrapper,
} from './LoginPage.styled';
import { useTranslation } from 'react-i18next';
import Input from 'components/Input/Input';
import Icon from 'components/Icon/Icon';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import Button from 'components/Button/Button';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { useEffect, useState } from 'react';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import Wrapper from '../Wrapper/Wrapper';
import { useNavigate } from 'react-router-dom';
import { RoutesConfig } from 'navigation/routes';
import PageNotFound from 'pages/PageNotFound/PageNotFound';
import i18n from 'providers/i18n/i18n';
import { useCompanyLogin } from './hooks';
import { saveActivationToken } from 'services/ActivationTokenService';
import { isEmailValid, isPasswordValid } from './helpers';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPhone } = useBreakpointFlag();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [hidePassword, setHidePassword] = useState<boolean>(true);
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const lang = searchParams.get('lang');
  const companyName = searchParams.get('companyName');

  usePageTitle('Login');

  const handleEyeClick = () => {
    setHidePassword(!hidePassword);
  };

  if (!token) {
    return <PageNotFound />;
  }

  const { mutate: companyLogin, isLoading } = useCompanyLogin();

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  const handleEnterKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    const emailValid = isEmailValid(email);
    const passwordValid = isPasswordValid(password);

    if (!emailValid || !passwordValid) {
      if (!email) {
        setEmailError(t('E-mail is required'));
      } else if (!emailValid) {
        setEmailError(t('Please enter a valid email'));
      } else {
        setEmailError('');
      }

      if (!password) {
        setPasswordError(t('Password is required'));
      } else if (!passwordValid) {
        setPasswordError(t('Password must be at least 8 characters'));
      } else {
        setPasswordError('');
      }

      return;
    }

    companyLogin(
      { email, password, token },
      {
        onSuccess: (data) => {
          saveActivationToken(data.activation_token);
          navigate(
            RoutesConfig.CompanyLoginSelect.buildPath(
              token,
              lang || 'en',
              companyName || '',
              data.user_id.toString()
            )
          );
        },
      }
    );
  };

  return (
    <Wrapper title={t('Log in')}>
      <Form>
        <InputWrapper>
          <Input
            height={'55rem'}
            placeholder={t('E-mail')}
            value={email}
            onChange={(e: any) => {
              const value = e.target.value;
              setEmail(value);
              if (!value || isEmailValid(value)) {
                setEmailError('');
              } else {
                setEmailError(t('Please enter a valid email'));
              }
            }}
            wrapperStyles={{ width: '100%' }}
            width={'100%'}
            autoComplete="email"
            errorMessage={emailError}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            height={'55rem'}
            placeholder={t('Password')}
            type={hidePassword ? 'password' : ''}
            value={password}
            onChange={(e: any) => {
              const value = e.target.value;
              setPassword(value);
              if (!value || isPasswordValid(value)) {
                setPasswordError('');
              } else {
                setPasswordError(t('Password must be at least 8 characters'));
              }
            }}
            wrapperStyles={{ width: '100%' }}
            width={'100%'}
            onKeyDown={handleEnterKeyPress}
            autoComplete="current-password"
            errorMessage={passwordError}
          />
          <EyeWrapper onClick={() => handleEyeClick()}>
            <Icon
              svg={hidePassword ? Eye : EyeSlash}
              color={COLORS.BLACK}
              size={20}
            />
          </EyeWrapper>
        </InputWrapper>
        <ForgotPasswordWrapper>
          <ForgotPasswordLabel
            onClick={() =>
              navigate(RoutesConfig.RequestPasswordResetPage.fullPath)
            }
          >
            {t('Forgot your password?')}
          </ForgotPasswordLabel>
        </ForgotPasswordWrapper>

        <ButtonContainer>
          <Button
            label={t('Continue')}
            primary
            disabled={
              isLoading || !isEmailValid(email) || !isPasswordValid(password)
            }
            width={isPhone ? '100%' : '580rem'}
            height={'55rem'}
            onClick={() => handleSubmit()}
          />
          <Button
            label={t('Create New Account')}
            secondary
            disabled={isLoading}
            width={isPhone ? '100%' : '580rem'}
            height={'55rem'}
            onClick={() =>
              navigate(
                RoutesConfig.CompanyLoginCreate.buildPath(
                  token,
                  lang || 'en',
                  companyName || ''
                )
              )
            }
          />
        </ButtonContainer>
      </Form>
    </Wrapper>
  );
};

export default LoginPage;
